import { SelectChangeEvent } from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { FC, useState } from 'react'
import { HardwareType, MarkupPropsType, MarkupType, MaterialGroupType, MaterialType } from '../../api/appAPI'
import { HardwareProps } from '../../classes/Hardware'
import { EnqueueSnackbarType, showMessage } from '../common/Helpers'
import HardwareUIBox, { ChipType, MaterialChipType } from './HardwareUIBox'

interface IProps {
    hardwareList: Array<HardwareType>
    markups: Array<MarkupType>
    materials: MaterialType[]
    materialGroups: MaterialGroupType[]

    addHardware(hardware: HardwareProps, enqueueSnackbar: EnqueueSnackbarType): void
    addMarkup(markup: MarkupPropsType, enqueueSnackbar: EnqueueSnackbarType): void
    openMaterialDetails(id: number): void
    openMaterialGroupDetails(id: number): void
}

const DefineHardware: FC<IProps> = ({ markups, addMarkup, addHardware, ...props }) => {
    const [name, setName] = useState('')
    const [sku, setSku] = useState('')
    const [description, setDescription] = useState('')
    const [unitWeight, setUnitWeight] = useState(0)
    const [unitPrice, setUnitPrice] = useState('')
    const [unitPriceAddon, setUnitPriceAddon] = useState('0')
    const [addonD, setAddonD] = useState('0')
    const [addonP, setAddonP] = useState('0')
    const [minPrice, setMinPrice] = useState('0')
    const [type, setType] = useState('')
    const [paramType, setParamType] = useState('')
    const [markupId, setMarkupId] = useState('')
    const [materialsChips, setMaterialsChips] = useState<Array<MaterialChipType>>([])
    const [defaultMaterialId, setDefaultMaterialId] = useState(0)
    const [childrenChips, setChildrenChips] = useState<Array<ChipType>>([])


    const { enqueueSnackbar } = useSnackbar()


    const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
        setName(event.target.value.replace(/['"]/, ''))
    }

    const handleSkuChange = (event: React.ChangeEvent<{ value: string }>) => {
        setSku(event.target.value.replace(/['"]/, ''))
    }

    const handleDescriptionChange = (event: React.ChangeEvent<{ value: string }>) => {
        setDescription(event.target.value.replace(/['"]/, ''))
    }

    const handleTypeChange = (event: SelectChangeEvent<string>) => {
        setType(event.target.value)
    }

    const handleParamTypeChange = (event: SelectChangeEvent<string>) => {
        setParamType(event.target.value)
    }

    const handleUnitWeightChange = (value: number) => {
        setUnitWeight(value)
    }

    const handlePriceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setUnitPrice(event.target.value as string)
    }

    const handlePriceAddonChange = (value: string) => {
        setUnitPriceAddon(value)
    }

    const handleAddonDChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setAddonD(event.target.value as string)
    }

    const handleAddonPChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setAddonP(event.target.value as string)
    }

    const handleMinPriceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setMinPrice(event.target.value as string)
    }

    const handleMarkupIdChange = (event: SelectChangeEvent<string>) => {
        setMarkupId(markups.find(m => m.name === event.target.value)?.id.toString() || '')
    }

    const deleteChildrenChip = (chip: ChipType) => {
        setChildrenChips(childrenChips.filter((ch) => ch.id !== chip.id))
    }

    const addChildrenChip = (chip: ChipType) => {
        setChildrenChips([...childrenChips, chip])
    }

    const deleteMaterialsChip = (chip: MaterialChipType) => {
        if (materialsChips.length === 1) {
            setUnitPrice('0')
            setDefaultMaterialId(0)
        }
        // else if (chip.id === defaultMaterialId) {
        //     const firstChip = materialsChips.find(chip => chip.id !== defaultMaterialId)
        //     setDefaultMaterialId(firstChip ? firstChip.id : 0)
        // }
        setMaterialsChips(materialsChips.filter((ch) => ch.id !== chip.id))
    }

    const addMaterialsChip = (chip: MaterialChipType) => {
        if (materialsChips.length === 0) {
            setUnitPrice('-1')
            setUnitWeight(-1)
            setDefaultMaterialId(chip.id)
        }
        setMaterialsChips([...materialsChips, chip])
    }

    const handleCreateClick = () => {
        if (name === '') {
            showMessage(enqueueSnackbar, "Enter part name")
            return
        }

        if (sku === '') {
            showMessage(enqueueSnackbar, "Enter part sku")
            return
        }

        if (type === '') {
            showMessage(enqueueSnackbar, "Select part type")
            return
        }

        if (type === 'parametric') {
            if (paramType === '') {
                showMessage(enqueueSnackbar, "Select part parameter type")
                return
            }
        }

        if (unitPrice === '') {
            showMessage(enqueueSnackbar, "Enter part unit price")
            return
        }

        if (markupId === '') {
            showMessage(enqueueSnackbar, "Select part markup")
            return
        }

        if (materialsChips.length > 0) {
            let defaultMaterialExist = false

            for (const mChip of materialsChips) {
                if (mChip.isGroup) {
                    for (const chip of mChip.materials) {
                        if (chip.id === defaultMaterialId) {
                            defaultMaterialExist = true
                            break
                        }
                    }
                    if (defaultMaterialExist) break
                }
                else {
                    if (mChip.id === defaultMaterialId) {
                        defaultMaterialExist = true
                        break
                    }
                }
            }

            if (!defaultMaterialExist) {
                showMessage(enqueueSnackbar, "Select default material")
                return
            }
        }

        if (type === 'static') {
            const hardwareProps = {
                name,
                sku,
                description,
                type,
                unitWeight,
                unitPrice: parseFloat(unitPrice),
                unitPriceAddon: parseFloat(unitPriceAddon),
                addonD: parseFloat(addonD),
                addonP: parseFloat(addonP),
                minPrice: parseFloat(minPrice),
                markupId: parseInt(markupId),
                materialsIds: materialsChips.map(chip => chip.isGroup ? 'g' + chip.id : chip.id.toString()),
                defaultMaterialId,
                childrenIds: childrenChips.map(chip => chip.id),
                editable: '1'
            } as HardwareProps
            addHardware(hardwareProps, enqueueSnackbar)
        }
        else if (type === 'parametric') {
            const hardwareProps = {
                name,
                sku,
                description,
                type: type + '_' + paramType,
                unitWeight,
                unitPrice: parseFloat(unitPrice),
                unitPriceAddon: parseFloat(unitPriceAddon),
                addonD: parseFloat(addonD),
                addonP: parseFloat(addonP),
                minPrice: parseFloat(minPrice),
                markupId: parseInt(markupId),
                materialsIds: materialsChips.map(chip => chip.isGroup ? 'g' + chip.id : chip.id.toString()),
                defaultMaterialId,
                childrenIds: childrenChips.map(chip => chip.id),
                editable: '1'
            } as HardwareProps
            setParamType('')
            addHardware(hardwareProps, enqueueSnackbar)
        }

        setName('')
        setSku('')
        setDescription('')
        setType('')
        setUnitWeight(0)
        setUnitPrice('')
        setUnitPriceAddon('')
        setAddonD('')
        setAddonP('')
        setMinPrice('')
        setMarkupId('')
        setChildrenChips([])
        setMaterialsChips([])
        setDefaultMaterialId(0)
    }

    return (
        <HardwareUIBox
            title='New Part'
            markups={markups}
            hardwareList={props.hardwareList}
            materials={props.materials}
            materialGroups={props.materialGroups}
            addMarkup={addMarkup}
            name={{
                value: name,
                onChange: handleNameChange
            }}
            sku={{
                value: sku,
                onChange: handleSkuChange
            }}
            description={{
                value: description,
                onChange: handleDescriptionChange
            }}
            type={{
                value: type,
                onChange: handleTypeChange
            }}
            paramType={{
                value: paramType,
                onChange: handleParamTypeChange
            }}
            unitWeight={{
                value: unitWeight,
                onChange: handleUnitWeightChange
            }}
            unitPrice={{
                value: unitPrice,
                onChange: handlePriceChange
            }}
            unitPriceAddon={{
                value: unitPriceAddon,
                onChange: handlePriceAddonChange
            }}
            addonD={{
                value: addonD,
                onChange: handleAddonDChange
            }}
            addonP={{
                value: addonP,
                onChange: handleAddonPChange
            }}
            minPrice={{
                value: minPrice,
                onChange: handleMinPriceChange
            }}
            markupId={{
                value: markupId,
                onChange: handleMarkupIdChange
            }}
            button={{
                text: 'Create',
                onClick: handleCreateClick
            }}
            childrenChips={{
                chips: childrenChips,
                deleteChip: deleteChildrenChip,
                addChip: addChildrenChip
            }}
            materialsChips={{
                chips: materialsChips,
                deleteChip: deleteMaterialsChip,
                addChip: addMaterialsChip
            }}
            defaultMaterialId={{
                value: defaultMaterialId,
                set: setDefaultMaterialId
            }}
            openMaterialDetails={props.openMaterialDetails}
            openMaterialGroupDetails={props.openMaterialGroupDetails}

            editable
        />
    )
}

export default DefineHardware